import React from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import AppDialog, { AppDialogHeader } from '../common/AppDialog';
import StatusChip from '../common/StatusChip';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { PaymentStatus, Sale } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/currency';
import { formatDateTime } from '../../utils/date';
import SaleItemsBreakdown from './SaleItemsBreakdown';

interface SaleSummaryDialogProps {
  open: boolean;
  sale: Sale | null;
  onClose: () => void;
}

const paymentStatusColor = (status: PaymentStatus): 'default' | 'success' | 'warning' | 'error' => {
  switch (status) {
    case PaymentStatus.PAID:
      return 'success';
    case PaymentStatus.PARTIAL:
      return 'warning';
    case PaymentStatus.UNPAID:
      return 'error';
    default:
      return 'default';
  }
};

const paymentStatusLabelKey = (status: PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.PAID:
      return 'sales.paid';
    case PaymentStatus.PARTIAL:
      return 'sales.partiallyPaid';
    case PaymentStatus.UNPAID:
      return 'sales.unpaid';
    default:
      return 'sales.paymentStatus';
  }
};

const SaleSummaryDialog: React.FC<SaleSummaryDialogProps> = ({ open, sale, onClose }) => {
  const { t, language } = useLanguage();

  return (
    <AppDialog presentation="workflow" open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="sale-summary-title">
      <AppDialogHeader id="sale-summary-title" icon={<CheckCircleIcon />}>
        {t('sales.saleSummary')}
      </AppDialogHeader>
      <DialogContent>
        {sale && (
          <Stack spacing={2.5} sx={{ mt: 1 }} data-testid="sale-summary">
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ border: theme => `1px solid ${theme.palette.success.main}` }}>
              {t('sales.saleCompletedSuccessfully')} — {t('sales.saleNumber')}: <strong>{sale.saleNumber}</strong>
            </Alert>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t('sales.customer')}</Typography>
                <Typography fontWeight="medium">{sale.customerName}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t('sales.saleDate')}</Typography>
                <Typography fontWeight="medium">
                  {formatDateTime(sale.saleDate, language, {
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('sales.paymentStatus')}</Typography>
                <Box><StatusChip color={paymentStatusColor(sale.paymentStatus)} label={t(paymentStatusLabelKey(sale.paymentStatus))} /></Box>
              </Box>
            </Stack>

            {sale.saleItems && sale.saleItems.length > 0 && (
              <SaleItemsBreakdown items={sale.saleItems} ariaLabel={t('sales.orderSummary')} />
            )}

            <Divider />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t('sales.netAmount')}</Typography>
                <Typography variant="h6" fontWeight={700}>{formatCurrency(sale.netAmount ?? sale.totalAmount - (sale.vatAmount ?? 0))}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t('sales.vatAmount')}</Typography>
                <Typography variant="h6" fontWeight={700}>{formatCurrency(sale.vatAmount ?? 0)}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t('sales.grossAmount')}</Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">{formatCurrency(sale.totalAmount)}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t('sales.paidAmount')}</Typography>
                <Typography variant="h6" fontWeight={700} color={sale.paidAmount > 0 ? 'success.main' : 'text.secondary'}>{formatCurrency(sale.paidAmount)}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t('sales.remainingAmount')}</Typography>
                <Typography variant="h6" fontWeight={700} color={sale.remainingAmount > 0 ? 'error.main' : 'text.secondary'}>{formatCurrency(sale.remainingAmount)}</Typography>
              </Box>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </AppDialog>
  );
};

export default SaleSummaryDialog;
